import type { Property } from '@unreserved/types';
import type { ListingsHomeDto } from '@unreserved/sdk';
import {
  mapListingTypeToSaleType,
  mapListingStatusToPropertyStatus,
  mapAuctionStatusToAuctionInfo,
} from '@unreserved/constants';
import { propertyDetailsUtils } from './property-details';

export const VENDOR_MANAGED_CAMPAIGN = 'vendor_managed' as const;


export function formatListingPublishedAtForDisplay(
  value: string | Date | null | undefined,
): string | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

type listingdata = ListingsHomeDto & {
  propertyMedia?: {
    mediaMetadata: {
      fileUrl: string;
      altText: string;
    };
  }[];
};

function lovableExtrasAddedOnForProperty(
  value: ListingsHomeDto['lovableExtrasAddedOn'],
): string | Date | null | undefined {
  if (value == null) return value;
  if (typeof value === 'string' || value instanceof Date) return value;
  return undefined;
}

// Calculate auction progress percentage
export function calculateAuctionProgress(
  startDate?: string | null,
  endDate?: string | null,
): number | undefined {
  if (!startDate || !endDate) return undefined;

  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();

  if (now < start) return 0;
  if (now > end) return 100;

  const total = end - start;
  const elapsed = now - start;
  return Math.round((elapsed / total) * 100);
}

function getListingPrice(listing: ListingsHomeDto): number {
  if (typeof listing.price === 'number') {
    return listing.price;
  }

  if (typeof listing.highestBidPrice === 'number') {
    return listing.highestBidPrice;
  }

  if (typeof listing.highestOfferPrice === 'number') {
    return listing.highestOfferPrice;
  }

  return 0;
}

export function mapListingsHomeDtoToProperty(listing: listingdata): Property {
  const listingType = listing.listingType;
  const listingStatus = listing.listingStatus;
  const auctionStatus = listing.auctionStatus ?? undefined;

  const price = getListingPrice(listing);
  const auctionStartPrice =
    listing.listingType === 'auction' ||
      listing.listingType === 'reverse_auction'
      ? listing.auctionStartPrice
      : undefined;

  const landArea = propertyDetailsUtils.toNumber(listing.landArea) ?? 0;

  const address =
    typeof listing.address === 'string' ? listing.address : undefined;

  const bedrooms = typeof listing.bedrooms === 'number' ? listing.bedrooms : 0;

  const bathrooms =
    typeof listing.bathrooms === 'number' ? listing.bathrooms : 0;

  const pricePerUnit = landArea > 0 ? Math.round(price / landArea) : 0;

  const saleType = mapListingTypeToSaleType(listingType);

  const status = mapListingStatusToPropertyStatus(
    listingType,
    listingStatus,
    auctionStatus,
  );

  const auctionStartDate =
    typeof listing.auctionStartDate === 'string'
      ? listing.auctionStartDate
      : '';

  const auctionInfo =
    listingType !== 'private_sale' && auctionStatus && auctionStartDate
      ? mapAuctionStatusToAuctionInfo(
        listingType,
        auctionStatus,
        auctionStartDate ?? undefined,
        undefined,
      )
      : undefined;

  const mediaMetadata =
    listing.media?.mediaMetadata || listing?.propertyMedia?.[0]?.mediaMetadata;
  const imageUrl =
    typeof mediaMetadata?.fileUrl === 'string' &&
      mediaMetadata.fileUrl.length > 0
      ? mediaMetadata.fileUrl
      : '';

  const imageAlt =
    typeof mediaMetadata?.altText === 'string'
      ? mediaMetadata.altText
      : listing.title;

  let bidLabel: string | undefined;
  let bidAmount: number | undefined;

  if (status === 'Sold') {
    const soldPrice =
      typeof listing.soldPrice === 'number' ? listing.soldPrice : null;
    const highestBidPrice =
      typeof listing.highestBidPrice === 'number'
        ? listing.highestBidPrice
        : null;
    const highestOfferPrice =
      typeof listing.highestOfferPrice === 'number'
        ? listing.highestOfferPrice
        : null;

    const amount = soldPrice ?? highestOfferPrice ?? highestBidPrice ?? null;

    if (amount != null) {
      bidLabel = 'Offer Amount';
      bidAmount = amount;
    }
  } else if (status === 'In Progress' || status === 'Under Offer') {
    const highestBidPrice =
      typeof listing.highestBidPrice === 'number'
        ? listing.highestBidPrice
        : null;
    const highestOfferPrice =
      typeof listing.highestOfferPrice === 'number'
        ? listing.highestOfferPrice
        : null;

    if (listingType === 'auction') {
      const amount = highestBidPrice ?? price;
      if (amount != null) {
        bidLabel = 'Highest Bid';
        bidAmount = amount;
      }
    } else if (listingType === 'private_sale') {
      const amount = highestOfferPrice ?? price;
      if (amount != null) {
        bidLabel = highestOfferPrice ? 'Highest Offer' : 'Offer Amount';
        bidAmount = amount;
      }
    }
  }

  const isAuctionLike =
    listingType === 'auction' || listingType === 'reverse_auction';

  let pricing: Property['pricing'];
  if (isAuctionLike) {
    pricing = { startingBid: auctionStartPrice as unknown as number };
  } else if (listingType === 'private_sale') {
    pricing = {
      startingPrice: price,
      priceText:
        typeof listing.priceText === 'string' ? listing.priceText : null,
      displayPrice: listing.displayPrice,
      isVendorManagedCampaign:
        listing.campaign === VENDOR_MANAGED_CAMPAIGN,
    };
  } else {
    pricing = { startingPrice: price };
  }

  const publishedAtRaw = listing.publishedAt;
  const publishedAt =
    typeof publishedAtRaw === 'string'
      ? publishedAtRaw
      : null;

  return {
    id: listing.id,
    slug: listing.slug ?? '',
    image: {
      url: imageUrl,
      alt: imageAlt ?? '',
    },
    saleType,
    status,
    listingStatus: listing.listingStatus ?? undefined,
    publishedAt,
    lovableExtras: listing.lovableExtras ?? null,
    auctionInfo,
    formattedTimeStartsIn: undefined,
    details: {
      title: listing.title ?? '',
      address,
      area: landArea,
      areaUnit: listing.landArea_unit ?? undefined,
      pricePerSqft: pricePerUnit,
      bedrooms,
      bathrooms,
    },
    bidInfo:
      bidLabel && bidAmount != null
        ? {
          label: bidLabel,
          amount: bidAmount,
        }
        : undefined,
    pricing,
    lovableExtrasAddedOn: lovableExtrasAddedOnForProperty(
      listing?.lovableExtrasAddedOn,
    ),
  };
}
